import { easeExpOut } from "d3-ease";
import * as React from "react";
import styled, { keyframes } from "styled-components";

import classNames from "classnames";

import { animated, ForwardedProps, useTrail } from "react-spring";

import { useSelector } from "react-redux";

import { useAnalyticsUtil } from "../";

import { favouritesStore, useThunkDispatch } from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";

const HoverAnimation = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
  }

  100% {
    transform: rotate(0deg) scale(1.2);
  }
`;

const ShakeAnimation = keyframes`
  0% {
    transform: rotate(-10deg) scale(0.9);
  }

  100% {
    transform: rotate(10deg) scale(0.9);
  }
`;

const PopShrinkAnimation = keyframes`
  0% {
    transform: rotate(0deg) scale(1.4);
  }

  100% {
    transform: rotate(0deg) scale(1);
  }
`;

const StyledFavBtn = styled.button`
  .heartSvg {
    transition: all 0.2s ${(p): string => p.theme.timingFnEaseOut};
  }

  &.mousedown {
    .heartSvg > path {
      fill: ${(p): string => p.theme.accentColor};
    }
  }

  &.mouseup {
    .heartSvg {
      animation: ${PopShrinkAnimation} 0.5s
        ${(p): string => p.theme.timingFnEaseOut};
    }
    &:hover {
      .heartSvg {
        animation: ${PopShrinkAnimation} 0.5s
          ${(p): string => p.theme.timingFnEaseOut};
      }
    }
  }

  &:hover {
    .heartSvg {
      animation: ${HoverAnimation} 0.3s
        ${(p): string => p.theme.timingFnEaseOut} forwards;
    }
  }

  &:active {
    .heartSvg {
      animation: ${ShakeAnimation} 0.1s ease-in-out infinite alternate !important;
    }
  }
`;

const StyledFavInner = styled.span<{
  $isFavourited: boolean;
}>`
  position: relative;
  font-weight: 400;
  pointer-events: none;

  color: ${(p) => p.theme.accentColor};

  .heartSvg > path {
    fill: ${(p) => (p.$isFavourited ? p.theme.accentColor : "transparent")};
  }

  &:hover {
    .heartSvg > path {
      fill: ${(p) => (p.$isFavourited ? p.theme.accentColor : "transparent")};
    }
  }
`;

const HeartSvg = styled(animated.svg)`
  position: absolute;
  top: 0;
  left: 0;
`;

const heartPath = (
  <path
    fillRule="evenodd"
    stroke="currentColor"
    strokeLinejoin="round"
    strokeWidth="1.5"
    d="M17.0277 6.14967C17.0277 9.40625 11.6758 13.4277 8.99995 15.0312C6.32402 13.4277 0.972168 9.40625 0.972168 6.14967C0.972168 2.07895 3.52647 0.96875 4.98606 0.96875C7.17545 0.96875 8.63505 2.81908 8.99995 3.92927C9.36484 2.81908 10.8244 0.96875 13.0138 0.96875C14.4735 0.96875 17.0277 2.07895 17.0277 6.14967Z"
    clipRule="evenodd"
  />
);

export interface BaseFavouriteBtnProps {
  animationDuration?: number;
  type: FAVOURITES_ENUM;
  isFavourited: boolean;
  onFavourite: () => void;
  onUnfavourite: () => void;
}

type AnimationTrail = ForwardedProps<{
  config: {
    duration: number;
    easing: (t: number) => number;
  };
  opacity: 0 | 1;
  y: number;
}>[];

export const BaseFavouriteBtn = ({
  animationDuration = 800,
  type,
  isFavourited,
  onFavourite,
  onUnfavourite,
}: BaseFavouriteBtnProps) => {
  const animationRef = React.useRef(null);

  const [isAnimating, setIsAnimating] = React.useState(false);
  const [mouseDown, setMouseDown] = React.useState(false);
  const [mouseUp, setMouseUp] = React.useState(false);

  const trail: AnimationTrail = useTrail(3, {
    config: {
      duration: animationDuration,
      easing: easeExpOut,
    },
    opacity: isAnimating ? 0 : 1,
    y: isAnimating ? 1 : 0,
  });

  const handleMouseDown = () => setMouseDown(true);
  const handleMouseUp = () => setMouseDown(false);
  const handleMouseOut = () => {
    if (mouseDown) {
      setMouseDown(false);
    }
  };

  const handleToggleFavourite = React.useCallback(() => {
    if (isFavourited) {
      onUnfavourite();
      return;
    }

    onFavourite();

    if (isAnimating) {
      clearTimeout(animationRef.current);
    }

    setMouseUp(true);
    setIsAnimating(true);

    animationRef.current = setTimeout(() => {
      setIsAnimating(false);
      setMouseUp(false);
    }, animationDuration);
  }, [
    animationDuration,
    isAnimating,
    isFavourited,
    onFavourite,
    onUnfavourite,
  ]);

  const BtnInner = () => {
    return (
      <StyledFavInner $isFavourited={isFavourited}>
        {/* Animating hearts you see on click */}
        {isAnimating && <BtnAnimation animationTrail={trail} />}

        {/* Svg Heart icon */}
        <svg
          css={`
            position: relative;
            top: -1px;
            margin-right: 0.5rem;
          `}
          width="18"
          height="16"
          id="icons:heart"
          className="heartSvg"
          viewBox="0 0 18 16"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          {heartPath}
        </svg>

        {/* Text for the favourites button */}
        {type === FAVOURITES_ENUM.OPPORTUNITY ? (
          <span>Favourite opportunity</span>
        ) : (
          <span>Favourite organisation</span>
        )}
      </StyledFavInner>
    );
  };

  return (
    <StyledFavBtn
      type="button"
      onClick={handleToggleFavourite}
      onMouseDown={handleMouseDown}
      onMouseOut={handleMouseOut}
      onMouseUp={handleMouseUp}
      className={classNames(
        "position-relative btn",
        mouseUp && "mouseup",
        mouseDown && "mousedown"
      )}
      aria-pressed={isFavourited ? "true" : "false"}
    >
      <BtnInner />
    </StyledFavBtn>
  );
};

// Container Favourites Btn

export interface FavouriteBtnProps {
  id: number;
  type: FAVOURITES_ENUM;
  onFavourite?: (isFavourited: boolean) => void;
}

export const FavouriteBtn = ({ id, type, onFavourite }: FavouriteBtnProps) => {
  const dispatch = useThunkDispatch();
  const onFavouriteRef = React.useRef(onFavourite);
  const { trackPageLink } = useAnalyticsUtil();

  const isFavourited = useSelector(
    favouritesStore.selectors.isFavourite(id, type)
  );

  React.useEffect(() => {
    onFavouriteRef.current = onFavourite;
  });

  const handleFavourite = React.useCallback(() => {
    dispatch(favouritesStore.actions.addFavourite(id, type));

    trackPageLink(
      type === FAVOURITES_ENUM.OPPORTUNITY
        ? "favourite-opportunity"
        : "favourite-organisation"
    );

    if (onFavouriteRef.current) {
      onFavouriteRef.current(true);
    }
  }, [dispatch, id, trackPageLink, type]);

  const handleUnfavourite = React.useCallback(() => {
    dispatch(favouritesStore.actions.removeFavourite(id, type));
    trackPageLink(
      type === FAVOURITES_ENUM.OPPORTUNITY
        ? "remove-favourite-opportunity"
        : "remove-favourite-organisation"
    );

    if (onFavouriteRef.current) {
      onFavouriteRef.current(false);
    }
  }, [dispatch, id, trackPageLink, type]);

  return (
    <BaseFavouriteBtn
      isFavourited={isFavourited}
      onFavourite={handleFavourite}
      onUnfavourite={handleUnfavourite}
      type={type}
    />
  );
};

interface BtnAnimationProps {
  animationTrail: AnimationTrail;
}

const BtnAnimation = (props: BtnAnimationProps) => {
  const seed1 = Math.random();
  const seed2 = Math.random();
  const seed3 = Math.random();

  return (
    <span
      aria-hidden="true"
      css={`
        position: absolute;
        fill: ${(p): string => p.theme.accentColor};
        top: 0;
        left: 0;
        display: inline-block;
      `}
    >
      {props.animationTrail.map(({ y, ...rest }, idx) => (
        <HeartSvg
          xmlns="http://www.w3.org/2000/svg"
          key={idx}
          style={{
            ...rest,
            // @ts-ignore
            transform: y.interpolate(
              (y) =>
                `translate3d(${
                  idx !== 0
                    ? y * Math.ceil(seed1 * 5 + 10) * (idx % 2 ? -1 : 1)
                    : 0
                }px , -${idx !== 0 ? y * 20 : y * 30}px, 0) scale(${
                  0.2 + y * 0.8
                }) rotate(${
                  idx !== 0
                    ? 45 - Math.ceil((idx === 1 ? seed2 : seed3) * 90)
                    : 0
                }deg)`
            ),
          }}
          width={18}
          height={16}
          aria-hidden
        >
          {heartPath}
        </HeartSvg>
      ))}
    </span>
  );
};
